import { UploadDynamicInventoryPolicy } from './dynamic-inventory-policy.types';
import { updateDynamicInventoryPolicyJson } from 'pages/master-data/general-configuration/general/importer-exporter-dynamic-inventory-policy/importer-exporter-dynamic-inventory-policy.service';
import intl from 'react-intl-universal';

export interface ProcessingResult {
  success: boolean;
  item: UploadDynamicInventoryPolicy;
  index: number;
  message: string;
  type: number;
}

export interface ProcessingSummary {
  successCount: number;
  errorCount: number;
  results: ProcessingResult[];
}

interface DuplicateGroup {
  material: string;
  plant: string;
  startDate: string;
  endDate: string;
  lineNumbers: number[];
}

export class DynamicInventoryPolicyProcessor {
  private readonly languageCode: string;

  constructor(
    private setProcessingPercentual: React.Dispatch<React.SetStateAction<number>>
  ) {
    const { getLanguageCode } = require('locales');
    this.languageCode = getLanguageCode();
  }

  private getDuplicateMaterialMessage(material: string, lines: string, startDate: string, endDate: string, languageCode: string): string {
    if (languageCode === 'pt') {
      return `Material ${material} possui informações repetidas nas linhas ${lines} para o período ${startDate} - ${endDate}`;
    } else if (languageCode === 'es') {
      return `Material ${material} contiene información repetida en las líneas ${lines} para el período ${startDate} - ${endDate}`;
    }
    return `Material ${material} has repeated information in lines ${lines} for period ${startDate} - ${endDate}`;
  }

  private validateDuplicateItems(items: UploadDynamicInventoryPolicy[]): DuplicateGroup[] {
    const duplicateMap = new Map<string, DuplicateGroup>();
    const { startDateField, endDateField, plantField } = this.getFieldNamesByLanguage();
    
    items.forEach((item, index) => {
      const startDate = this.formatDate(item[startDateField] ?? '');
      const endDate = this.formatDate(item[endDateField] ?? '');
      const key = `${item['Material']}_${item[plantField]}_${startDate}_${endDate}`;
      if (!duplicateMap.has(key)) {
        duplicateMap.set(key, {
          material: item['Material'] ?? '',
          plant: item[plantField] ?? '',
          startDate: item[startDateField] ?? '',
          endDate: item[endDateField] ?? '',
          lineNumbers: [index + 1]
        });
      } else {
        duplicateMap.get(key)!.lineNumbers.push(index + 1);
      }
    });

    return Array.from(duplicateMap.values())
      .filter(group => group.lineNumbers.length > 1);
  }

  async processItems(fileData: UploadDynamicInventoryPolicy[]): Promise<ProcessingSummary> {
    const summary: ProcessingSummary = {
      successCount: 0,
      errorCount: 0,
      results: []
    };

    const duplicateGroups = this.validateDuplicateItems(fileData);
    if (duplicateGroups.length > 0) {
      this.updateProgress(fileData.length, fileData.length);

      const messages = duplicateGroups.map(group => {
        const lines = group.lineNumbers.map(x => x + 1).join(', ');
        return {
          firstLineDuplicated: group.lineNumbers[0],
          message: this.getDuplicateMaterialMessage(group.material, lines, group.startDate, group.endDate, this.languageCode)
        }
      });

      return {
        successCount: 0,
        errorCount: fileData.length,
        results: messages.map((x, i) => ({
          success: false,
          item: fileData[x.firstLineDuplicated],
          index: i,
          message: x.message,
          type: 0
        }))
      };
    }

    for (let i = 0; i < fileData.length; i++) {
      const result = await this.processSingleItem(fileData[i], i);
      
      if (result.success) {
        summary.successCount++;
      } else {
        summary.errorCount++;
      }
      
      summary.results.push(result);
      this.updateProgress(i + 1, fileData.length);
    }

    return summary;
  }

  private async processSingleItem(item: UploadDynamicInventoryPolicy, index: number): Promise<ProcessingResult> {
    try {
      const itemData = this.convertItemToBackendFormat(item);
      this.validateItemRequiredFields(itemData);
      
      const response = await updateDynamicInventoryPolicyJson(itemData);
      
      if (response.AllSuccess) {
        return this.createSuccessResult(item, index);
      } else {
        return this.createErrorResultFromResponse(item, index, response);
      }
      
    } catch (error: any) {
      return this.handleItemError(error, item, index);
    }
  }

  private handleItemError(error: any, item: UploadDynamicInventoryPolicy, index: number): ProcessingResult {
    console.error(`Erro ao processar item ${index + 1}:`, error);
    console.error('Item data:', item);
    
    const errorMessage = this.formatErrorMessage(error);
    const message = this.createResultMessage(index, item, errorMessage);
    
    return {
      success: false,
      item,
      index,
      message,
      type: 0
    };
  }

  private updateProgress(current: number, total: number): void {
    const progress = Math.round((current / total) * 100);
    this.setProcessingPercentual(progress);
  }

  buildResponse(summary: ProcessingSummary): any[] {
    const summaryMessage = this.createSummaryMessage(summary.successCount);
    
    const finalResults = [
      {
        Type: summary.successCount > 0 ? 2 : 0,
        Message: summaryMessage
      },
      ...summary.results.map(result => ({
        Type: result.type,
        Message: result.message
      }))
    ];

    return [{
      AllSuccess: summary.errorCount === 0 && summary.successCount > 0,
      Results: finalResults
    }];
  }

  private convertItemToBackendFormat(item: UploadDynamicInventoryPolicy): any {
    const { minStockField, maxStockField, replenishmentField, plantField, startDateField, endDateField } = this.getFieldNamesByLanguage();
    
    const minimumStock = item[minStockField] ? parseFloat(item[minStockField].replace(',', '.')) : null;
    const maximumStock = item[maxStockField] ? parseFloat(item[maxStockField].replace(',', '.')) : null;
    const targetStockPoint = item[replenishmentField] ? parseFloat(item[replenishmentField].replace(',', '.')) : null;

    return {
      material: item['Material'],
      plant: item[plantField] ?? '',
      startDate: this.formatDate(item[startDateField]),
      endDate: this.formatDate(item[endDateField]),
      minimumStock: minimumStock ?? 0,
      maximumStock: maximumStock ?? 0,
      targetStockPoint: targetStockPoint ?? 0
    };
  }

  private getPlantLabel(languageCode: string): string {
    if (languageCode === 'pt') return 'Planta';
    if (languageCode === 'es') return 'Planta';
    return 'Unit';
  }

  private getStartDateLabel(languageCode: string): string {
    if (languageCode === 'pt') return 'Data Início';
    if (languageCode === 'es') return 'Fecha de Inicio';
    return 'Start Date';
  }

  private getEndDateLabel(languageCode: string): string {
    if (languageCode === 'pt') return 'Data Término';
    if (languageCode === 'es') return 'Fecha de término';
    return 'End Date';
  }

  private getErrorPrefix(languageCode: string): string {
    if (languageCode === 'pt') return 'Campos obrigatórios faltando';
    if (languageCode === 'es') return 'Faltan campos obligatorios';
    return 'Missing required fields';
  }

  private validateItemRequiredFields(itemData: any): void {
    const validations = [
      { field: 'material', value: itemData.material, label: () => 'Material' },
      { field: 'plant', value: itemData.plant, label: () => this.getPlantLabel(this.languageCode) },
      { field: 'startDate', value: itemData.startDate, label: () => this.getStartDateLabel(this.languageCode) },
      { field: 'endDate', value: itemData.endDate, label: () => this.getEndDateLabel(this.languageCode) }
    ];

    const missingFields = validations
      .filter(validation => !validation.value)
      .map(validation => validation.label());

    if (missingFields.length > 0) {
      const prefix = this.getErrorPrefix(this.languageCode);
      throw new Error(`${prefix}: ${missingFields.join(', ')}`);
    }
  }

  private getDefaultErrorMessage(languageCode: string): string {
    if (languageCode === 'pt') return 'Erro ao importar';
    if (languageCode === 'es') return 'Error al importar';
    return 'Import error';
  }

  private formatErrorMessage(error: any): string {
    if (error.response) {
      let errorMessage = `Request failed with status code ${error.response.status}`;
      if (error.response.data?.message) {
        errorMessage += ` - ${error.response.data.message}`;
      }
      return errorMessage;
    }
    
    return error.message || this.getDefaultErrorMessage(this.languageCode);
  }

  private createResultMessage(index: number, item: UploadDynamicInventoryPolicy, message: string): string {
    const identifier = this.getItemIdentifier(item);
    return `Item ${index + 1}: ${identifier} - ${message}`;
  }

  private getSuccessMessage(languageCode: string): string {
    if (languageCode === 'pt') return 'Importado com sucesso';
    if (languageCode === 'es') return 'Importado con éxito';
    return 'Successfully imported';
  }

  private createSuccessResult(item: UploadDynamicInventoryPolicy, index: number): ProcessingResult {
    const successMessage = this.getSuccessMessage(this.languageCode);
    const message = this.createResultMessage(index, item, successMessage);
    
    return {
      success: true,
      item,
      index,
      message,
      type: 2
    };
  }

  private createErrorResultFromResponse(item: UploadDynamicInventoryPolicy, index: number, response: any): ProcessingResult {
    const errorMessages = response.Results.map((result: any) => result.Message).join('; ');
    const message = this.createResultMessage(index, item, errorMessages);
    
    return {
      success: false,
      item,
      index,
      message,
      type: response.Results[0]?.Type || 0
    };
  }

  private createSummaryMessage(successCount: number): string {
    return successCount > 0 
      ? `${intl.get('master-data.general-configuration.totalSuccessMessage')} ${successCount}`
      : intl.get('master-data.general-configuration.noRecordsWereImported');
  }

  private getItemIdentifier(item: UploadDynamicInventoryPolicy): string {
    return item['Material'] ?? item['Código do Material'] ?? item['Código Material'] ?? 'Item sem código';
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return '';
    
    if (dateStr.includes('T') && dateStr.includes('Z')) {
      const date = new Date(dateStr);
      return date.toISOString();
    }
    
    try {
      let parts: string[];
      let day: number;
      let month: number;
      let year: number;
      
      if (dateStr.includes(' ')) {
        dateStr = dateStr.split(' ')[0];
      }
      
      if (dateStr.includes('/')) {
        parts = dateStr.split('/');
        
        // Spanish/European format (DD/MM/YYYY)
        if (this.languageCode === 'es' || this.languageCode === 'pt') {
          [day, month, year] = parts.map(p => parseInt(p, 10));
        } else {
          // US format (MM/DD/YYYY)
          [month, day, year] = parts.map(p => parseInt(p, 10));
        }
        
        // Adjust month (0-based)
        month = month - 1;
        
        const date = new Date(Date.UTC(year, month, day));
        if (isNaN(date.getTime())) {
          console.warn(`Invalid date format: ${dateStr}`);
          return dateStr;
        }
        
        return date.toISOString();
      }
      
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date format: ${dateStr}`);
        return dateStr;
      }
      
      return date.toISOString();
    } catch (e) {
      console.warn(`Error parsing date: ${dateStr}`, e);
      return dateStr;
    }
  }

  private getFieldNamesByLanguage() {
    let minStockField = 'Minimum Stock';
    let maxStockField = 'Maximum Stock';
    let replenishmentField = 'Supply Point';
    let plantField = 'Unit';
    let startDateField = 'Start Date';
    let endDateField = 'End Date';
    if (this.languageCode === 'pt') {
      minStockField = 'Estoque Mínimo';
      maxStockField = 'Estoque Máximo';
      replenishmentField = 'Ponto Reabastecimento';
      plantField = 'Planta';
      startDateField = 'Data Início';
      endDateField = 'Data Término';
    } else if (this.languageCode === 'es') {
      minStockField = 'Stock Mínimo';
      maxStockField = 'Stock Máximo';
      replenishmentField = 'Punto de Reabastecimiento';
      plantField = 'Planta';
      startDateField = 'Fecha de Inicio';
      endDateField = 'Fecha de Término';
    }

    return { minStockField, maxStockField, replenishmentField, plantField, startDateField, endDateField };
  }
}
